import axios from 'axios';
import { stripSandboxLabel } from '../utils/tenantDetection';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Resolve tenant identifier dynamically from URL.
 * Priority:
 *   1. Subdomain (e.g., acme.contractflow.com → 'acme')
 *   2. Query param ?tenant=acme (useful for testing)
 *   3. REACT_APP_TENANT_ID env variable
 *   4. Fallback to 'demo'
 */
function resolveTenantId() {
  // 1. Check subdomain — sandbox hosts carry an extra leading label
  //    (sandbox.acme.example.com) that must not be read as the tenant.
  const hostname = stripSandboxLabel(window.location.hostname);
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const sub = parts[0];
    // Ignore common non-tenant subdomains
    if (!['www', 'api', 'app', 'localhost'].includes(sub) && sub !== hostname) {
      return sub;
    }
  }

  // 2. Check URL query param (e.g., ?tenant=acme) — development only.
  //    L: in production the tenant is the subdomain, full stop; a link with
  //    ?tenant= must not be able to point a session at another tenant.
  if (process.env.NODE_ENV !== 'production') {
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');
    if (tenantParam) return tenantParam;
  }

  // 3. Env variable
  if (process.env.REACT_APP_TENANT_ID) return process.env.REACT_APP_TENANT_ID;

  // 4. Fallback
  return 'demo';
}

const TENANT_ID = resolveTenantId();

// Export tenant ID so other parts of the app can use it
export const currentTenantId = TENANT_ID;

// ─────────────────────────────────────────────────────────────
// Token storage — per-tab for impersonation, shared for normal.
// When sessionStorage has `isImpersonation === 'true'`, we use
// sessionStorage exclusively for this tab so the tenant owner's
// localStorage-based session in other tabs stays intact.
// ─────────────────────────────────────────────────────────────
export function isImpersonating() {
  try { return sessionStorage.getItem('isImpersonation') === 'true'; }
  catch { return false; }
}

// A delegation (Vekalet) tab is per-tab for the same reason an impersonation
// tab is: the delegate's own session lives in localStorage and must survive
// them stepping into someone else's workspace in another tab.
export function isDelegated() {
  try { return sessionStorage.getItem('isDelegation') === 'true'; }
  catch { return false; }
}

// Either kind of stand-in session takes over this tab; everything else reads
// the shared localStorage session.
function isScopedSession() {
  return isImpersonating() || isDelegated();
}

export function tokenStore() {
  return isScopedSession() ? sessionStorage : localStorage;
}

export function setTokens({ accessToken, refreshToken }, { impersonation = false, delegation = false } = {}) {
  const scoped = impersonation || delegation;
  const store = scoped ? sessionStorage : localStorage;
  // A tab is exactly one kind of session. This matters because window.open
  // COPIES the opener's sessionStorage into the new tab: a delegation tab
  // opened from inside an impersonation tab inherits a stale isImpersonation
  // flag (and vice versa), which would mislabel the session everywhere the
  // flags are read. Claiming one kind always clears the other.
  if (impersonation) {
    sessionStorage.setItem('isImpersonation', 'true');
    sessionStorage.removeItem('isDelegation');
  }
  if (delegation) {
    sessionStorage.setItem('isDelegation', 'true');
    sessionStorage.removeItem('isImpersonation');
  }
  store.setItem('accessToken', accessToken);
  store.setItem('refreshToken', refreshToken);
}

export function clearTokens() {
  const store = tokenStore();
  store.removeItem('accessToken');
  store.removeItem('refreshToken');
  if (isImpersonating()) sessionStorage.removeItem('isImpersonation');
  if (isDelegated()) sessionStorage.removeItem('isDelegation');
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json', 'x-tenant-id': TENANT_ID },
});

// Request interceptor - attach auth token from the active store (session in
// impersonation tabs, local otherwise).
api.interceptors.request.use((config) => {
  const token = tokenStore().getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A single in-flight refresh shared by every 401'd request. Parallel refreshes
// race: the first rotates the refresh token, the second then posts the stale
// token, fails, and wrongly logs the user out (seen when a page fires several
// requests at once after the access token expires).
let refreshPromise = null;

// Response interceptor - handle 401, refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh & login redirect for public external-review endpoints
    const isExternalReview = originalRequest?.url?.includes('/external-review/access/');
    if (isExternalReview) {
      return Promise.reject(error);
    }

    // Skip refresh for the impersonation consume endpoint. A 401 here means
    // the handoff token itself is invalid/used — refreshing would rotate the
    // owner's refresh token (since sessionStorage isn't set yet on this tab)
    // and risk clearing the owner's session on any follow-up failure.
    const isConsume = originalRequest?.url?.includes('/auth/impersonate/consume')
      || originalRequest?.url?.includes('/auth/delegation/consume');
    if (isConsume) {
      return Promise.reject(error);
    }

    // A delegation that expired or was revoked mid-session comes back as a 401
    // the refresh flow can never fix — refreshing re-checks the same delegation
    // and fails the same way. Drop the tab's session and let the handoff page
    // (or the banner's exit) explain it, instead of looping on refresh.
    if (error.response?.data?.code === 'DELEGATION_INVALID') {
      clearTokens();
      if (!window.location.pathname.startsWith('/delegation/handoff')) {
        window.location.href = '/login?delegationEnded=1';
      }
      return Promise.reject(error);
    }

    // SEC-14: the backend now enforces the forced password change server-side
    // and answers every other request with 403 PASSWORD_CHANGE_REQUIRED. Send
    // the user to the change-password page (once — not while already there).
    if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
      if (window.location.pathname !== '/change-password-required') {
        window.location.assign('/change-password-required');
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const store = tokenStore();
        if (!refreshPromise) {
          const refreshToken = store.getItem('refreshToken');
          refreshPromise = refreshToken
            ? axios.post(`${API_BASE}/auth/refresh`, { refreshToken }, {
                headers: { 'x-tenant-id': TENANT_ID },
              }).finally(() => { refreshPromise = null; })
            : null;
        }
        if (refreshPromise) {
          const res = await refreshPromise;
          store.setItem('accessToken', res.data.accessToken);
          store.setItem('refreshToken', res.data.refreshToken);
          originalRequest.headers.Authorization = `Bearer ${res.data.accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        // Don't redirect to login on public pages or the impersonation handoff
        // page — the handoff page renders its own error UI so the user sees
        // the real failure reason instead of being dumped at /login.
        const path = window.location.pathname;
        if (
          !path.startsWith('/external-review/') &&
          !path.startsWith('/impersonate/handoff')
        ) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => {
    const refreshToken = tokenStore().getItem('refreshToken');
    return api.post('/auth/logout', { refreshToken });
  },
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  // Impersonation — tenant owner requests a handoff token for target user.
  impersonate: (userId) => api.post(`/auth/impersonate/${userId}`),
  // Consumed on the new tab, exchanges handoff token for real credentials.
  consumeImpersonation: (handoffToken) => api.post('/auth/impersonate/consume', { handoffToken }),
  // Delegation (Vekalet) — the delegate requests a handoff token for a
  // delegation granted to them, then consumes it on the new tab.
  enterDelegation: (delegationId) => api.post(`/auth/delegation/${delegationId}`),
  consumeDelegation: (handoffToken) => api.post('/auth/delegation/consume', { handoffToken }),
};

// Delegations API (Vekalet)
export const delegationsAPI = {
  // Which of the two authorities the current user is able to hand over.
  grantable: () => api.get('/delegations/grantable'),
  // Delegations I granted — Profil > Vekalet.
  mine: () => api.get('/delegations/mine'),
  // Delegations granted to me that are usable right now — the user menu.
  received: () => api.get('/delegations/received'),
  candidates: (q, signal) => api.get('/delegations/candidates', { params: { q }, signal }),
  create: (data) => api.post('/delegations', data),
  update: (id, data) => api.put(`/delegations/${id}`, data),
  revoke: (id) => api.delete(`/delegations/${id}`),
};

// Contracts API
export const contractsAPI = {
  list: (params) => api.get('/contracts', { params }),
  // Summary counters for the list's stat cards — always the user's full totals,
  // never narrowed by the list filters.
  stats: () => api.get('/contracts/stats'),
  search: (q, signal) => api.get('/contracts/search', { params: { q }, signal }),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => {
    if (data instanceof FormData) {
      return api.post('/contracts', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/contracts', data);
  },
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  upload: (id, formData) => api.post(`/contracts/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadUrl: (contractId, docId) => `${API_BASE}/contracts/${contractId}/documents/${docId}/download`,
  submitReview: (id) => api.post(`/contracts/${id}/submit-review`),
  requestEdit: (id) => api.post(`/contracts/${id}/request-edit`),
  addComment: (id, data) => api.post(`/contracts/${id}/comments`, data),
  timeline: (id) => api.get(`/contracts/${id}/timeline`),
  recordView: (id) => api.post(`/contracts/${id}/view`),
  viewHistory: (id) => api.get(`/contracts/${id}/view-history`),
  // ── Contract Update & Version Management ──────────────────────────────────
  // Amendment / Addendum: clones the current version into a new pending
  // version that re-runs the contract's workflow from the start.
  createVersion: (id, data) => api.post(`/contracts/${id}/versions`, data),
  // All major versions of the contract's family (original included).
  versions: (id) => api.get(`/contracts/${id}/versions`),
  // Responsible Person Update: no new version, no workflow restart.
  updateResponsible: (id, data) => api.post(`/contracts/${id}/responsible`, data),
};

// Templates API
export const templatesAPI = {
  list: (params) => api.get('/templates', { params }),
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Workflows API
export const workflowsAPI = {
  list: (params) => api.get('/workflows', { params }),
  get: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
  duplicate: (id, data) => api.post(`/workflows/${id}/duplicate`, data),
  // Live runs blocking edits of a definition, and the tenant-owner-only kill
  // that clears one (pending approvals close as Deleted, contract → draft).
  activeInstances: (id) => api.get(`/workflows/${id}/active-instances`),
  killInstance: (instanceId) => api.post(`/workflows/instances/${instanceId}/kill`),
  start: (contractId, data) => api.post(`/workflows/start/${contractId}`, data),
  approve: (approvalId, data) => api.post(`/workflows/approve/${approvalId}`, data),
  reject: (approvalId, data) => api.post(`/workflows/reject/${approvalId}`, data),
  // Ends the run and returns the contract to its initiator for edits. They
  // resend it with `start` afterwards, which runs the workflow from step 1.
  requestChanges: (approvalId, data) => api.post(`/workflows/request-changes/${approvalId}`, data),
  // Signature-stage counterpart of requestChanges: the initiator pulls the
  // contract back for revision instead of signing it.
  returnToRevision: (approvalId, data) => api.post(`/workflows/return-to-revision/${approvalId}`, data),
  // The holder of a returned contract drops it instead of sending it again —
  // the open request is cleared and the contract goes back to being a draft.
  cancelChangeRequest: (contractId) => api.post(`/workflows/change-request/${contractId}/cancel`),
  pendingApprovals: () => api.get('/workflows/approvals/pending'),
  // Approvals + opinions + returned contracts in one response — what the
  // dashboard widget, sidebar badge and notifier share (see tasksSlice).
  pendingTasks: () => api.get('/workflows/tasks/pending'),
  // Görüş Al — non-blocking opinion requests raised by a step's action owner.
  requestOpinion: (approvalId, data) => api.post(`/workflows/opinions/request/${approvalId}`, data),
  respondOpinion: (opinionId, data) => api.post(`/workflows/opinions/${opinionId}/respond`, data),
  pendingOpinions: () => api.get('/workflows/opinions/pending'),
  contractOpinions: (contractId) => api.get(`/workflows/opinions/contract/${contractId}`),
  opinionDepartmentUsers: (departmentId) => api.get('/workflows/opinions/department-users', { params: { departmentId } }),
  // Yönlendir — hand my own active action to another user (mandatory reason).
  forward: (approvalId, data) => api.post(`/workflows/forward/${approvalId}`, data),
  // Contracts sent back to me for changes — work waiting on me with no
  // approval record behind it.
  pendingEdits: () => api.get('/workflows/approvals/pending-edits'),
  approvalHistory: () => api.get('/workflows/approvals/history'),
  // Template maintenance: now-inactive users still named in workflow
  // definitions, and the bulk swap that replaces them.
  inactiveAssignees: () => api.get('/workflows/maintenance/inactive-assignees'),
  replaceAssignee: (data) => api.post('/workflows/maintenance/replace-assignee', data),
};

// Task Reassignment API (Access & Compliance → İş Devri)
export const reassignmentAPI = {
  // Source picker may include deactivated users (the leaver); the target
  // picker passes activeOnly = true.
  searchUsers: (q, activeOnly = false) =>
    api.get('/workflows/reassignment/users', { params: { q, ...(activeOnly ? { activeOnly: 1 } : {}) } }),
  // Users who actually hold open tasks, with their counts — the ... action.
  workload: () => api.get('/workflows/reassignment/workload'),
  // Preload one user by id (the screen opened from a deactivation notification).
  getUser: (userId) => api.get('/workflows/reassignment/users', { params: { userId } }),
  tasks: (userId) => api.get(`/workflows/reassignment/tasks/${userId}`),
  reassign: (data) => api.post('/workflows/reassignment/reassign', data),
};

// Users API
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  search: (q, limit = 5) => api.get('/users/search', { params: { q, limit } }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  deletePermanent: (id) => api.delete(`/users/${id}/permanent`),
};

// Dashboard API
export const dashboardAPI = {
  summary: () => api.get('/dashboard/summary'),
  expiring: (days) => api.get('/dashboard/expiring', { params: { days } }),
  reports: (period) => api.get('/dashboard/reports', { params: { period } }),
};

// Notifications API
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Audit API
export const auditAPI = {
  list: (params) => api.get('/audit', { params }),
  forEntity: (type, id) => api.get(`/audit/entity/${type}/${id}`),
};

// Activity Report API (Faaliyet Raporu)
export const activityReportAPI = {
  meta: (params) => api.get('/activity-report/meta', { params }),
  report: (params) => api.get('/activity-report', { params }),
  exportPdf: (params) => api.get('/activity-report/export/pdf', { params, responseType: 'blob' }),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  getBranding: () => api.get('/settings/branding'),
  updateOrganization: (data) => api.put('/settings/organization', data),
  updateBranding: (data) => api.put('/settings/branding', data),
  // `kind` is 'logo' | 'hero' — the server validates dimensions per kind and
  // returns the hosted URL to store in branding.
  uploadBrandingImage: (kind, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);
    return api.post('/settings/branding/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addDepartment: (data) => api.post('/settings/departments', data),
  updateDepartment: (id, data) => api.put(`/settings/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/settings/departments/${id}`),
  updateThresholds: (data) => api.put('/settings/thresholds', data),
  savePermissions: (data) => api.put('/settings/permissions', data),
  getRoles: () => api.get('/settings/roles'),
  updateLanguage: (data) => api.put('/settings/language', data),
  updateDefaults: (data) => api.put('/settings/defaults', data),
};

// Obligations API
export const obligationsAPI = {
  list: (params) => api.get('/obligations', { params }),
  get: (id) => api.get(`/obligations/${id}`),
  create: (data) => api.post('/obligations', data),
  update: (id, data) => api.put(`/obligations/${id}`, data),
  complete: (id, data) => api.put(`/obligations/${id}/complete`, data),
  delete: (id) => api.delete(`/obligations/${id}`),
  overdue: () => api.get('/obligations/overdue'),
  upcoming: (days) => api.get('/obligations/upcoming', { params: { days } }),
};

// Contract Documents API (ONLYOFFICE integration)
export const documentsAPI = {
  // Document CRUD
  list: (contractId) => api.get(`/contracts/${contractId}/documents`),
  get: (contractId, documentId) => api.get(`/contracts/${contractId}/documents/${documentId}`),
  // `data` may carry a `category` of 'draft' | 'signed' | 'supporting', which
  // files the document into the contract's matching standard folder.
  create: (contractId, data) => {
    if (data instanceof FormData) {
      return api.post(`/contracts/${contractId}/documents`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post(`/contracts/${contractId}/documents`, data);
  },

  // Editor session
  getEditorSession: (contractId, documentId) =>
    api.get(`/contracts/${contractId}/documents/${documentId}/editor-session`),

  // Explicit save — triggers OnlyOffice forcesave through the Command Service.
  // documentKey must be the key the live editor session is running under,
  // otherwise OnlyOffice can't find the open document and reports "no changes".
  save: (contractId, documentId, documentKey) =>
    api.post(`/contracts/${contractId}/documents/${documentId}/save`, { documentKey }),

  // Versions
  getVersions: (contractId, documentId) =>
    api.get(`/contracts/${contractId}/documents/${documentId}/versions`),
  uploadVersion: (contractId, documentId, formData) =>
    api.post(`/contracts/${contractId}/documents/${documentId}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  downloadVersion: (contractId, documentId, versionNumber) =>
    `${API_BASE}/contracts/${contractId}/documents/${documentId}/versions/${versionNumber}/download`,

  // Diff/Compare
  compare: (contractId, documentId, v1, v2) =>
    api.get(`/contracts/${contractId}/documents/${documentId}/compare`, { params: { v1, v2 } }),

  // Presence
  getPresence: (contractId, documentId) =>
    api.get(`/contracts/${contractId}/documents/${documentId}/presence`),

  // PDF downloads
  downloadPdfUrl: (contractId, documentId) =>
    `${API_BASE}/contracts/${contractId}/documents/${documentId}/download-pdf`,
  downloadSignedUrl: (contractId, documentId) =>
    `${API_BASE}/contracts/${contractId}/documents/${documentId}/download-signed`,

  // In-app read-only viewer — streams a PDF rendering (DOCX converted server-side)
  viewPdf: (contractId, documentId, versionNumber) =>
    api.get(`/contracts/${contractId}/documents/${documentId}/view-pdf`, {
      params: versionNumber ? { version: versionNumber } : {},
      responseType: 'blob',
    }),
};

// Document Library API (tenant-wide folder view)
export const documentLibraryAPI = {
  // Root: the hierarchy's groups with counts, standalone folders and totals.
  // Contract folders are not in it — see listContractFolders.
  listFolders: (params) => api.get('/document-library', { params }),
  // One page of contract folders under a counterparty bucket.
  // { companyKey, counterpartyCode, page, limit, search, sort, companyId }
  listContractFolders: (params) => api.get('/document-library/contracts', { params }),
  // A single contract's folder card (editor return, search-hit navigation).
  getContractCard: (contractId) => api.get('/document-library/contracts', { params: { contractId } }),
  // Advanced search: metadata + full text + OCR, permission-scoped server-side.
  // types: comma list of metadata,fulltext,ocr (omitted = all).
  advancedSearch: (q, types, signal) =>
    api.get('/document-library/search', {
      params: { q, ...(types ? { types } : {}) },
      signal,
    }),
  getFolder: (contractId) => api.get(`/document-library/${contractId}`),
  createFolder: (contractId, data) => api.post(`/document-library/${contractId}/folders`, data),
  uploadFile: (contractId, file, folderId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folderId', folderId);
    return api.post(`/document-library/${contractId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Standalone (non-contract) library folders + files
  getStandaloneFolder: (folderId) => api.get(`/document-library/folders/${folderId}`),
  createStandaloneFolder: (data) => api.post('/document-library/folders', data),
  uploadStandaloneFile: (folderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/document-library/folders/${folderId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadStandaloneFile: (fileId) =>
    api.get(`/document-library/files/${fileId}/download`, { responseType: 'blob' }),
};

// Roles API (HR Management)
export const rolesAPI = {
  list: (params) => api.get('/roles', { params }),
  memberOptions: () => api.get('/roles/member-options'),
  get: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Companies API (HR Management — central Company master)
export const companiesAPI = {
  list: (params) => api.get('/companies', { params }),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  deactivate: (id) => api.put(`/companies/${id}/deactivate`),
};

// User Groups API
export const groupsAPI = {
  list: (params) => api.get('/groups', { params }),
  get: (id) => api.get(`/groups/${id}`),
  create: (data) => api.post('/groups', data),
  update: (id, data) => api.put(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
  addMembers: (groupId, userIds) => api.post(`/groups/${groupId}/members`, { userIds }),
  removeMember: (groupId, userId) => api.delete(`/groups/${groupId}/members/${userId}`),
};

// Departments API (HR Management)
export const departmentsAPI = {
  list: (params) => api.get('/departments', { params }),
  get: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Positions API (HR Management)
export const positionsAPI = {
  list: (params) => api.get('/positions', { params }),
  get: (id) => api.get(`/positions/${id}`),
  create: (data) => api.post('/positions', data),
  update: (id, data) => api.put(`/positions/${id}`, data),
  delete: (id) => api.delete(`/positions/${id}`),
};

// Titles API (HR Management)
export const titlesAPI = {
  list: (params) => api.get('/titles', { params }),
  get: (id) => api.get(`/titles/${id}`),
  create: (data) => api.post('/titles', data),
  update: (id, data) => api.put(`/titles/${id}`, data),
  delete: (id) => api.delete(`/titles/${id}`),
};

// Parameters API (Admin Management Panel)
export const parametersAPI = {
  // Currencies
  currencies: {
    list: (params) => api.get('/parameters/currencies', { params }),
    get: (id) => api.get(`/parameters/currencies/${id}`),
    create: (data) => api.post('/parameters/currencies', data),
    update: (id, data) => api.put(`/parameters/currencies/${id}`, data),
    deactivate: (id) => api.put(`/parameters/currencies/${id}/deactivate`),
  },
  // Contract Main Groups
  contractMainGroups: {
    list: (params) => api.get('/parameters/contract-main-groups', { params }),
    get: (id) => api.get(`/parameters/contract-main-groups/${id}`),
    create: (data) => api.post('/parameters/contract-main-groups', data),
    update: (id, data) => api.put(`/parameters/contract-main-groups/${id}`, data),
    deactivate: (id) => api.put(`/parameters/contract-main-groups/${id}/deactivate`),
  },
  // Supporting Document Types (Destekleyici Ek Türleri)
  supportingDocTypes: {
    list: (params) => api.get('/parameters/supporting-doc-types', { params }),
    get: (id) => api.get(`/parameters/supporting-doc-types/${id}`),
    create: (data) => api.post('/parameters/supporting-doc-types', data),
    update: (id, data) => api.put(`/parameters/supporting-doc-types/${id}`, data),
    deactivate: (id) => api.put(`/parameters/supporting-doc-types/${id}/deactivate`),
  },
  // Dynamic Metadata Types
  dynamicMetadataTypes: {
    list: (params) => api.get('/parameters/dynamic-metadata-types', { params }),
    get: (id) => api.get(`/parameters/dynamic-metadata-types/${id}`),
    create: (data) => api.post('/parameters/dynamic-metadata-types', data),
    update: (id, data) => api.put(`/parameters/dynamic-metadata-types/${id}`, data),
    deactivate: (id) => api.put(`/parameters/dynamic-metadata-types/${id}/deactivate`),
  },
  // Contract Types
  contractTypes: {
    list: (params) => api.get('/parameters/contract-types', { params }),
    get: (id) => api.get(`/parameters/contract-types/${id}`),
    create: (data) => api.post('/parameters/contract-types', data),
    update: (id, data) => api.put(`/parameters/contract-types/${id}`, data),
    deactivate: (id) => api.put(`/parameters/contract-types/${id}/deactivate`),
    getConfig: (id) => api.get(`/parameters/contract-types/${id}/config`),
  },
  // Approval Roles
  approvalRoles: {
    list: (params) => api.get('/parameters/approval-roles', { params }),
    get: (id) => api.get(`/parameters/approval-roles/${id}`),
    create: (data) => api.post('/parameters/approval-roles', data),
    update: (id, data) => api.put(`/parameters/approval-roles/${id}`, data),
    deactivate: (id) => api.put(`/parameters/approval-roles/${id}/deactivate`),
  },
  // Flow Controls
  flowControls: {
    list: (params) => api.get('/parameters/flow-controls', { params }),
    get: (id) => api.get(`/parameters/flow-controls/${id}`),
    create: (data) => api.post('/parameters/flow-controls', data),
    update: (id, data) => api.put(`/parameters/flow-controls/${id}`, data),
    deactivate: (id) => api.put(`/parameters/flow-controls/${id}/deactivate`),
  },
  // Counterparty Types
  counterpartyTypes: {
    list: (params) => api.get('/parameters/counterparty-types', { params }),
    get: (id) => api.get(`/parameters/counterparty-types/${id}`),
    create: (data) => api.post('/parameters/counterparty-types', data),
    update: (id, data) => api.put(`/parameters/counterparty-types/${id}`, data),
    deactivate: (id) => api.put(`/parameters/counterparty-types/${id}/deactivate`),
  },
  // Counterparties
  counterparties: {
    list: (params) => api.get('/parameters/counterparties', { params }),
    get: (id) => api.get(`/parameters/counterparties/${id}`),
    create: (data) => api.post('/parameters/counterparties', data),
    update: (id, data) => api.put(`/parameters/counterparties/${id}`, data),
    deactivate: (id) => api.put(`/parameters/counterparties/${id}/deactivate`),
  },
  // Field Schemas (Fields Manager)
  fieldSchemas: {
    list: (params) => api.get('/parameters/field-schemas', { params }),
    get: (id) => api.get(`/parameters/field-schemas/${id}`),
    create: (data) => api.post('/parameters/field-schemas', data),
    update: (id, data) => api.put(`/parameters/field-schemas/${id}`, data),
    deactivate: (id) => api.put(`/parameters/field-schemas/${id}/deactivate`),
    duplicate: (id, data) => api.post(`/parameters/field-schemas/${id}/duplicate`, data),
    delete: (id) => api.delete(`/parameters/field-schemas/${id}`),
  },
};

// Contract Templates API (document-based field mapping)
export const contractTemplatesAPI = {
  list: (params) => api.get('/contract-templates', { params }),
  get: (id) => api.get(`/contract-templates/${id}`),
  create: (formData) => api.post('/contract-templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/contract-templates/${id}`, data),
  duplicate: (id) => api.post(`/contract-templates/${id}/duplicate`),
  delete: (id) => api.delete(`/contract-templates/${id}`),
  match: (id, formData) => api.post(`/contract-templates/${id}/match`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (id) => api.get(`/contract-templates/${id}/download`, { responseType: 'blob' }),
};

// Template Library API (Sözleşme Şablon Kütüphanesi — downloadable templates)
export const templateLibraryAPI = {
  list: (params) => api.get('/template-library', { params }),
  create: (formData) => api.post('/template-library', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/template-library/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deactivate: (id) => api.put(`/template-library/${id}/deactivate`),
  remove: (id) => api.delete(`/template-library/${id}`),
  download: (id) => api.get(`/template-library/${id}/download`, { responseType: 'blob' }),
};

// Public QR verification (no auth — tenant resolved from subdomain)
export const verificationAPI = {
  get: (code) => api.get(`/verify/${code}`),
  downloadDocument: (code) => api.get(`/verify/${code}/document`, { responseType: 'blob' }),
};

// Signing API (Normal flow)
export const signingAPI = {
  getLockedDocuments: (contractId) => api.get(`/signing/locked-documents/${contractId}`),
  initiate: (data) => api.post('/signing/initiate', data),
  uploadSigned: (contractId, formData) => api.post(`/signing/upload-signed/${contractId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  status: (contractId) => api.get(`/signing/status/${contractId}`),
  // Completed-signing summary for the signed-document viewer's signature panel
  signatureInfo: (contractId) => api.get(`/signing/signature-info/${contractId}`),
  cancel: (signingRequestId) => api.post(`/signing/cancel/${signingRequestId}`),
  // Public (token-based) endpoints
  verifyAccess: (accessToken) => api.get(`/signing/access/${accessToken}`),
  verifyOtp: (accessToken, otpCode) => api.post(`/signing/access/${accessToken}/verify-otp`, { otpCode }),
  resendOtp: (accessToken) => api.post(`/signing/access/${accessToken}/resend-otp`),
  getPdf: (accessToken) => api.get(`/signing/access/${accessToken}/pdf`, { responseType: 'blob' }),
  submitSignature: (accessToken, data) => api.post(`/signing/access/${accessToken}/sign`, data),
  getSignedPdf: (accessToken) => api.get(`/signing/access/${accessToken}/signed-pdf`, { responseType: 'blob' }),
};

// DocuSign Signing API
export const docusignAPI = {
  listTemplates: () => api.get('/docusign/templates'),
  getTemplate: (templateId) => api.get(`/docusign/templates/${templateId}`),
  send: (data) => api.post('/docusign/send', data),
  checkStatus: (signingRequestId) => api.post(`/docusign/check-status/${signingRequestId}`),
};

// Signature Integrations API
export const signatureIntegrationsAPI = {
  list: () => api.get('/signature-integrations'),
  get: (id) => api.get(`/signature-integrations/${id}`),
  connect: (data) => api.post('/signature-integrations', data),
  update: (id, data) => api.put(`/signature-integrations/${id}`, data),
  test: (id) => api.post(`/signature-integrations/${id}/test`),
  disconnect: (id) => api.delete(`/signature-integrations/${id}`),
};

// External Review API
export const externalReviewAPI = {
  // Authenticated endpoints (internal users)
  invite: (data) => api.post('/external-review/invite', data),
  getForContract: (contractId) => api.get(`/external-review/contract/${contractId}`),

  // Public endpoints (external party — token-based, no JWT)
  verifyAccess: (accessToken) => api.get(`/external-review/access/${accessToken}`),
  verifyOTP: (accessToken, otpCode) => api.post(`/external-review/access/${accessToken}/verify-otp`, { otpCode }),
  resendOTP: (accessToken) => api.post(`/external-review/access/${accessToken}/resend-otp`),
  getDocuments: (accessToken) => api.get(`/external-review/access/${accessToken}/documents`),
  downloadDocumentUrl: (accessToken, documentId) => `${API_BASE}/external-review/access/${accessToken}/documents/${documentId}/download`,
  getEditorSession: (accessToken, documentId) => api.get(`/external-review/access/${accessToken}/documents/${documentId}/editor-session`),
  // Explicit save — triggers OnlyOffice forcesave for the external party's live
  // session. documentKey must match the key the client-side editor is actually
  // running under (same key as internal users — they share the room).
  save: (accessToken, documentId, documentKey) =>
    api.post(`/external-review/access/${accessToken}/documents/${documentId}/save`, { documentKey }),
  addComment: (accessToken, data) => api.post(`/external-review/access/${accessToken}/comments`, data),
  completeReview: (accessToken) => api.post(`/external-review/access/${accessToken}/complete`),
};

// AI API (contract analysis + DeepSeek-powered Form Builder)
export const aiAPI = {
  analyze: (data) => api.post('/ai/analyze', data),
  summarize: (text) => api.post('/ai/summarize', { text }),
  detectRisks: (text) => api.post('/ai/detect-risks', { text }),
  extractMetadata: (text) => api.post('/ai/extract-metadata', { text }),
  // Form Builder AI (DeepSeek)
  generateSchema: (message, history, currentSchema) =>
    api.post('/ai/generate-schema', { message, history, currentSchema }),
};

// Subscription API (tenant plan + user usage stats)
// SEC-30: plan changes are a platform-admin operation; PUT /subscription no
// longer exists, so there is deliberately no updatePlan here.
export const subscriptionAPI = {
  get: () => api.get('/subscription'),
};

// L: authenticated binary download through the shared instance, so the
// impersonation/delegation token store and the 401→refresh interceptor apply
// (raw fetch() + localStorage.getItem('accessToken') bypassed both). `url` is
// one of the absolute *Url helpers above, which already carry API_BASE — hence
// baseURL is cleared so axios does not prefix it a second time.
export const downloadBlob = (url) => api.get(url, { baseURL: '', responseType: 'blob' });

export default api;
